<template>
  <div class="flex flex-col gap-3">
    <FileUpload
      name="fileupload"
      accept="image/*,.csv,.doc,.docx,.pdf"
      :mode="props.uploadMode"
      :auto="props.uploadMode === 'basic'"
      :custom-upload="true"
      :maxFileSize="104857600"
      @uploader="uploadFiles"
    >
      <template v-if="props.uploadMode === 'advanced'" #empty>
        <p>Drag and drop files to here to upload.</p>
      </template>
    </FileUpload>
    <template v-if="files.length > 0">
      <SelectButton v-model="displayMode" :options="displayOptions" option-value="value" class="self-center" :allow-empty="false">
        <template #option="slotProps">
          <span class="me-2">{{ slotProps.option.value }}</span>
          <i :class="slotProps.option.icon"></i>
        </template>
      </SelectButton>
      <div v-if="displayMode === 'Grid'" class="flex felx-row flex-wrap gap-3 justify-center">
        <Card
          v-for="file in files"
          class="w-48"
          pt:content:class="hidden"
          pt:title:class="text-base font-bold overflow-hidden"
          pt:header:class="flex justify-center"
          pt:body:class="p-2!"
        >
          <template #header>
            <div class="w-full relative">
              <Button
                icon="pi pi-download"
                severity="success"
                class="absolute! w-8 h-8 z-10 m-1 rounded-full! drop-shadow-md"
                @click="downloadFile(file)"
              ></Button>
              <Button
                icon="pi pi-times"
                severity="danger"
                class="absolute! w-8 h-8 z-10 m-1 right-0 rounded-full! drop-shadow-md"
                @click="askDeleteFile(file)"
              ></Button>
              <Image
                v-if="isImage(file.fileType) && imgSrc.has(file.filename)"
                :src="imgSrc.get(file.filename)"
                :alt="file.filename"
                class="flex bg-surface-100 dark:bg-surface-800"
                imageClass="mx-auto h-40"
                preview
                pt:button:style="width: 100%"
              />
              <div v-if="!isImage(file.fileType) || !imgSrc.has(file.filename)" class="flex h-40 bg-surface-100 dark:bg-surface-800">
                <i class="pi pi-file text-9xl m-auto"></i>
              </div>
            </div>
          </template>
          <template #title>{{ file.filename }}</template>
          <template #subtitle>
            <span class="text-sm -mb-2">Added: {{ format(file.dateStamp, "MM/dd/yyyy") }}</span>
          </template>
        </Card>
      </div>
      <DataTable v-if="displayMode === 'Table'" :value="files" size="small" showGridlines scrollable scroll-height="250px">
        <template #empty> There are no files to display </template>
        <template #loading> Loading files, please wait. </template>
        <Column field="dateStamp" header="Date" :sortable="true"
          ><template #body="{ data }">
            {{ format(data.dateStamp, "MM/dd/yyyy") }}
          </template></Column
        >
        <Column field="filename" header="Filename" :sortable="true"></Column>
        <Column header="Download"
          ><template #body="{ data }">
            <div class="flex justify-center">
              <Button icon="pi pi-download" rounded aria-label="Download" @click="downloadFile(data)" />
            </div> </template
        ></Column>
        <Column header="Delete"
          ><template #body="{ data }">
            <div class="flex justify-center">
              <Button icon="pi pi-times" severity="danger" rounded aria-label="Delete" @click="askDeleteFile(data)" />
            </div> </template
        ></Column>
      </DataTable>
    </template>
  </div>
</template>

<script setup lang="ts">
import { FileEntity, getBasePath } from "@/enums/FileEntity";
import { isImage } from "@/enums/FileType";
import { fileSaveAs } from "@/services/Utility";
import { FileListSchema, type File } from "@/types/File";
import { format } from "date-fns";
import { useConfirm } from "primevue/useconfirm";

interface Props {
  filesUrl: string;
  uploadMode: "advanced" | "basic";
  fileEntityType: FileEntity;
}

const files = ref<File[]>([]);
const props = defineProps<Props>();
const deleteConfirm = useConfirm();
const displayMode = ref<"Table" | "Grid">(props.uploadMode === "advanced" ? "Grid" : "Table");
const imgSrc = ref(new Map<string, string>());
const basePath = computed<string | undefined>(() => getBasePath(props.fileEntityType));

const displayOptions = [
  {
    value: "Table",
    icon: "pi pi-align-justify",
  },
  {
    value: "Grid",
    icon: "pi pi-th-large",
  },
];

watch(
  () => props.filesUrl,
  () => getFiles()
);

onBeforeMount(async (): Promise<void> => {
  getFiles();
});

const getFiles = (): void => {
  for (let objectUrl of imgSrc.value.values()) {
    window.URL.revokeObjectURL(objectUrl);
  }

  imgSrc.value.clear();

  axios
    .get(props.filesUrl)
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          parseData(FileListSchema, data, files);

          files.value
            ?.filter((file) => isImage(file.fileType))
            .forEach((file) => {
              setPreviewImageSource(file);
            });
        },
        (errors: ApiError[]) => {
          error("Documents Error", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const uploadFiles = async (event: any): Promise<void> => {
  await axios
    .post(
      `${props.filesUrl}/upload`,
      {
        files: event.files,
      },
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    )
    .then((response) => {
      handleApiResponse(
        response,
        () => {
          getFiles();
          success("Success", "File uploaded");
        },
        () => {
          error("Error", "An error occurred while uploading files");
        }
      );
    })
    .catch((e) => errorApi(e));
};

const downloadFile = (file: File): void => {
  fileSaveAs(`/${basePath.value}/${file.fileId}/download`, file.filename);
};

const askDeleteFile = (file: File): void => {
  confirmPopup(
    deleteConfirm,
    "Delete file?",
    "Are you sure that you want to delete this file?",
    () => deleteFile(file),
    () => info("Action Cancelled", "File not deleted")
  );
};

const deleteFile = (file: File): void => {
  axios
    .delete(`/${basePath.value}/${file.fileId}/delete`)
    .then((response) => {
      handleApiResponse(
        response,
        () => {
          getFiles();
          success("Success", "File deleted");
        },
        () => {
          error("Error", "An error occurred while deleting file");
        }
      );
    })
    .catch((e) => errorApi(e));
};

const setPreviewImageSource = (file: File): void => {
  axios({
    method: "get",
    url: `/${basePath.value}/${file.fileId}/download`,
    responseType: "blob",
  }).then((response) => {
    imgSrc.value.set(file.filename, window.URL.createObjectURL(new Blob([response.data])));
  });
};
</script>
